import "./prism-retrieval.css"

import { useTranslation } from "react-i18next"

import type { FilthSearchActivityState } from "../../utils/filth-search-activity"

import { CircleCheckSolid } from "@/components/icons"
import { usePrismSequence } from "@/features/chat/hooks/use-prism-sequence"
import { usePrismSettleHeight } from "@/features/chat/hooks/use-prism-settle-height"
import { usePrismNamedBefore } from "@/features/chat/hooks/use-prism-turn"
import { cn } from "@/lib/utils"

import { PrismPanelHeader } from "./prism-panel-header"
import {
  PRISM_DEPTH_PLACEHOLDERS_MAX,
  PRISM_DEPTH_PLACEHOLDERS_PENDING,
  PRISM_DEPTH_STEP_MS,
  PRISM_RESERVE_COUNT,
  type PrismSource,
} from "./prism-retrieval-constants"
import { getPrismBadge, getPrismPlaceholderCount, getPrismStepCap } from "./prism-retrieval-utils"
import { PrismSourceChip } from "./prism-source-chip"

type PrismDepthPanelProps = {
  state: FilthSearchActivityState
  sources: PrismSource[]
  isLive: boolean
  /** The answer's citation click handler; makes each named source a button. */
  onSourceClick?: (citationIndex: number) => void
}

/**
 * Skärpedjupet: a field of soft, textless shapes that a scan line sweeps once
 * per checked step; the answer's cited sources then resolve sharp (Focus)
 * while the shapes recede. Only cited sources are ever named. The summary
 * line reports only counts FILTH sent and the number of cited sources.
 *
 * The live turn reserves its grounded structure from mount: a fixed set of
 * field cells that fill in or stay empty, the step row, and the summary line
 * below it. Grounding changes content, never structure, so the answer below
 * never jumps, and knowledge spaces reads as done before the summary joins.
 * Cited sources take the first cells in place, on top of the placeholders
 * they replace, which recede beneath them. A finished turn draws only its
 * content, and the live turn settles into exactly that layout when it ends:
 * the reserved cells close on Focus rather than at once.
 */
export function PrismDepthPanel({ state, sources, isLive, onSourceClick }: PrismDepthPanelProps) {
  const { t } = useTranslation()
  const total = PRISM_DEPTH_STEP_MS.length
  const cap = getPrismStepCap(state, total)
  const sequence = usePrismSequence(isLive, PRISM_DEPTH_STEP_MS, { cap, memoryId: "depth" })
  // Named before the message remounted: already on screen, so no entrance.
  const namedBefore = usePrismNamedBefore()
  const done = Math.min(sequence.step, cap)
  // The grounded state, once the sequence has reached it; undefined before.
  const resolved = state.phase === "grounded" && done >= total ? state : undefined
  const recede = resolved !== undefined || sources.length > 0
  const placeholders = getPrismPlaceholderCount(state, sources.length, {
    max: PRISM_DEPTH_PLACEHOLDERS_MAX,
    pending: PRISM_DEPTH_PLACEHOLDERS_PENDING,
  })
  // The live structure is held while the turn is live, then released.
  const reserve = sequence.mountedLive && isLive
  const fieldRef = usePrismSettleHeight<HTMLDivElement>(sequence.running, sequence.motion)
  const filledCells = sources.length + placeholders
  const cells = reserve ? Math.max(PRISM_DEPTH_PLACEHOLDERS_MAX, filledCells) : filledCells
  const searching = t("activity_messages.filth.searching", {
    defaultValue: "Searching company knowledge",
  })
  const writing = t("activity_messages.filth.writingAnswer", {
    count: state.phase === "grounded" ? state.evidenceCount : PRISM_RESERVE_COUNT,
    defaultValue: "Writing the answer · {{count}} evidence items",
  })
  const grounded = t("activity_messages.filth.grounded", {
    defaultValue: "Answer grounded in permitted evidence",
  })
  // Between grounding and the first named source, the answer is being written.
  let subtitle = searching
  if (resolved && sequence.running && sources.length === 0) subtitle = writing
  else if (resolved) subtitle = grounded
  const steps = [
    t("activity_messages.filth.steps.identity", { defaultValue: "Identity" }),
    t("activity_messages.filth.steps.sourceAccess", { defaultValue: "Source access" }),
    t("activity_messages.filth.steps.spaces", { defaultValue: "Knowledge spaces" }),
  ]

  return (
    <section
      aria-label={t("activity_messages.filth.depthView", { defaultValue: "Depth of field" })}
      data-prism-animated={sequence.motion}
      data-testid="prism-depth-panel"
      className={cn(
        "bg-card grid min-w-0 gap-3 rounded-xl border p-3.5",
        sequence.motion && "prism-motion"
      )}
    >
      <PrismPanelHeader
        badge={getPrismBadge(t, state)}
        badgeDone={resolved !== undefined}
        reserve={sequence.mountedLive ? [writing, grounded] : undefined}
        subtitle={subtitle}
      />

      <div ref={fieldRef} className="prism-settle relative">
        {sequence.running && done < total && (
          <span
            key={`scan-${sequence.step}`}
            aria-hidden="true"
            className="prism-scan bg-foreground"
            data-testid="prism-scan"
          />
        )}
        {/* Cells are keyed by position and never change size: a cited source
            is pulled into focus on top of its cell's placeholder, which
            recedes beneath it (Focus), and every other cell keeps its spot. */}
        <ol
          className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-1.5"
          aria-label={
            sources.length > 0
              ? t("activity_messages.filth.sourcesInFocus", {
                  count: sources.length,
                  defaultValue: "{{count}} sources in focus",
                })
              : undefined
          }
        >
          {Array.from({ length: cells }, (_, cell) => {
            const source = sources.at(cell)
            const filled = cell < filledCells
            return (
              <li
                key={`cell-${cell}`}
                aria-hidden={source ? undefined : true}
                className="relative h-8 min-w-0"
                data-prism-placeholder={filled && !source ? "" : undefined}
                data-prism-slot={`cell-${cell}`}
              >
                {(!source || sequence.motion) && (
                  <span
                    className="prism-placeholder prism-shape bg-card flex h-8 items-center gap-1.5 rounded-lg border px-2"
                    data-covered={source ? true : undefined}
                    data-empty={!filled}
                    data-prism-shape=""
                    data-recede={recede}
                  >
                    <span className="bg-secondary h-4 w-6 flex-none rounded-md" />
                    <span
                      className="bg-muted h-2 rounded-full"
                      style={{ width: `${45 + ((cell * 17) % 40)}%` }}
                    />
                  </span>
                )}
                {source && (
                  <PrismSourceChip
                    source={source}
                    className={cn(
                      "border-foreground bg-card absolute inset-0 flex min-w-0 items-center gap-1.5 rounded-lg border px-2 text-xs",
                      sequence.motion && cell >= namedBefore && "prism-focus-pull"
                    )}
                    onOpen={onSourceClick}
                  >
                    <span className="border-foreground bg-primary text-primary-foreground flex-none rounded-md border px-1.5 py-0.5 text-[10px] font-bold tabular-nums">
                      {source.n}
                    </span>
                    <span className="truncate">{source.title}</span>
                  </PrismSourceChip>
                )}
              </li>
            )
          })}
        </ol>
      </div>

      <ol
        aria-label={t("activity_messages.filth.stepsLabel", { defaultValue: "Prism steps" })}
        className="text-muted-foreground flex flex-wrap gap-x-3.5 gap-y-1 text-xs"
      >
        {/* Each label reserves its semibold width and the check its slot from
            mount, so a step turning done never pushes the steps after it. */}
        {steps.map((label, index) => (
          <li
            key={label}
            data-state={index < done ? "done" : "pending"}
            className={cn(
              "prism-guide inline-flex items-center gap-1",
              index < done && "text-foreground font-semibold"
            )}
          >
            <span className="prism-label prism-label--semibold" data-label={label}>
              {label}
            </span>
            <CircleCheckSolid
              aria-hidden="true"
              className={cn("prism-guide size-3", index < done ? "opacity-100" : "opacity-0")}
            />
          </li>
        ))}
      </ol>

      {(reserve || resolved) && (
        <p
          data-testid="prism-depth-summary"
          // Joins one Guiding beat after the last step reads as done.
          style={sequence.entrance ? { animationDelay: "var(--dur-guiding)" } : undefined}
          className={cn(
            "text-muted-foreground flex min-h-4 flex-wrap gap-x-3.5 gap-y-1 text-xs tabular-nums",
            resolved && sequence.entrance && "prism-guide-in"
          )}
        >
          {resolved && (
            <>
              <span>
                {t("activity_messages.filth.filesCount", {
                  count: resolved.fileCount,
                  defaultValue: "{{count}} files",
                })}
              </span>
              <span>
                {t("activity_messages.filth.claimsCount", {
                  count: resolved.claimCount,
                  defaultValue: "{{count}} claims",
                })}
              </span>
              <span>
                {t("activity_messages.filth.relatedCount", {
                  count: resolved.relatedCount,
                  defaultValue: "{{count}} related links",
                })}
              </span>
            </>
          )}
          {/* Last, so the counts before it never move when the sources land. */}
          {resolved && sources.length > 0 && (
            <span className={cn(sequence.motion && namedBefore === 0 && "prism-guide-in")}>
              {t("activity_messages.filth.sourcesInFocus", {
                count: sources.length,
                defaultValue: "{{count}} sources in focus",
              })}
            </span>
          )}
        </p>
      )}
    </section>
  )
}
